import React, { useEffect, useState, useRef } from 'react';
import "./index.css"
import { UserIcon } from 'lucide-react';
import minusIcon from './assets/minus-icons.png';
import sendIcon from './assets/send.png';
import aiSparkChatbotAlt from './assets/ai-spark-chatbot-alt.png';
const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const uploadsUrl = import.meta.env.VITE_UPLOADS_URL || 'http://localhost:3000/uploads';
interface Message {
  text: string
  isUser: boolean
  nextSteps?: Array<{
    type: string;
    label: string;
    action: string;
  }>
}

interface ChatbotTheme {
  data: {
    themeColor: string;
    logo: string;
    name: string;
    website_url: string;
  }
}

interface FormField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  value?: boolean;
}

interface ChatResponse {
  sessionId: string;
  question?: string;
  isLastQuestion?: boolean;
  isComplete?: boolean;
  recommendation?: string;
  message?: string;
  clarification?: string;
  messages?: Array<{
    type: string;
    content: string;
    isLastQuestion?: boolean;
  }>;
  suggestedServices?: string[];
  nextSteps?: Array<{
    type: string;
    label: string;
    action: string;
  }>;
  currentQuestion?: string;
  needsForm?: boolean;
  formFields?: FormField[];
  greeting?: string;
  data?: {
    isSetupIncomplete?: boolean;
    contactInfo?: string;
  }
  sessionEnded: boolean;
}

interface FormData {
  [key: string]: string | boolean;
}

// Error Boundary Component
class ChatbotErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: any, info: any) {
    // You can log error here
    // console.error('Chatbot rendering error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, color: 'red', fontWeight: 600 }}>
          Oops! Something went wrong in the chatbot. Please refresh the page or try again later.
        </div>
      );
    }
    return this.props.children;
  }
}

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [themeColor, setThemeColor] = useState('#007bff'); // Default color
  const [logo, setLogo] = useState('');
  const [name, setName] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string>('');
  const [isChatComplete, setIsChatComplete] = useState(false);
  const [chatDisabled, setChatDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<FormData>({});
  const [formError, setFormError] = useState("")
  const [anyButtonClicked, setAnyButtonClicked] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [isLoading]);

  // Defensive: Helper to safely get array
  const safeArray = (val: any) => Array.isArray(val) ? val : [];
  // Defensive: Helper to safely get string
  const safeString = (val: any, fallback = '') => typeof val === 'string' ? val : fallback;

  useEffect(() => {
    const fetchThemeColor = async () => {
      try {
        const storedBusinessId = sessionStorage.getItem('wellnex_business_id');
        if (storedBusinessId) {
          const response = await fetch(`${apiUrl}/chatbot/getChatBotDetail`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ businessId: storedBusinessId }),
          });

          let data: any = {};
          try {
            data = await response.json();
          } catch (e) {
            setMessages([{ text: 'Sorry, failed to load chatbot details. Please try again later.', isUser: false }]);
            setChatDisabled(true);
            return;
          }

          if (!data || typeof data !== 'object') {
            setMessages([{ text: 'Sorry, chatbot data is unavailable.', isUser: false }]);
            setChatDisabled(true);
            return;
          }

          if (!data.status) {
            setMessages([{ text: 'Sorry, this chatbot is currently unavailable. Please contact the business administrator for assistance.', isUser: false }]);
            setChatDisabled(true);
            return;
          }

          if (data.data) {
            if (!data.data.subscription) {
              setMessages([{ text: 'Sorry, this chatbot is currently unavailable. Please contact the business administrator for assistance.', isUser: false }]);
              setChatDisabled(true);
            } else {
              setChatDisabled(false);
            }
            if (data.data.themeColor) {
              setThemeColor(safeString(data.data.themeColor, '#007bff'));
            }
            if (data.data.logo) {
              setLogo(safeString(data.data.logo));
            }
            if (data.data.name) {
              setName(safeString(data.data.name));
            }
            setShowWelcomePopup(true);
          }
        }
      } catch (error) {
        setMessages([{ text: 'Error loading chatbot theme. Please try again later.', isUser: false }]);
        setChatDisabled(true);
      }
    };
    fetchThemeColor();
  }, []);
  // Removed hardcoded test data - the real chat starts via startChat useEffect below
  useEffect(() => {
    const startChat = async () => {
      if (isOpen) {
        try {
          const storedBusinessId = sessionStorage.getItem('wellnex_business_id');
          if (storedBusinessId) {
            const response = await fetch(`${apiUrl}/chatbot/start-chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ businessId: storedBusinessId }),
            });
            let data: any = {};
            try {
              data = await response.json();
            } catch (e) {
              setMessages([{ text: 'Sorry, failed to start chat. Please try again later.', isUser: false }]);
              setIsChatComplete(true);
              return;
            }
            if (!data || typeof data !== 'object') {
              setMessages([{ text: 'Sorry, invalid response from server.', isUser: false }]);
              setIsChatComplete(true);
              return;
            }
            if (data?.data?.isSetupIncomplete) {
              setMessages([{ text: `${safeString(data.messages)}\n\nContact: ${safeString(data.data.contactInfo)}`, isUser: false }]);
              setIsChatComplete(true);
              return;
            }
            setSessionId(safeString(data.sessionId));
            if (safeArray(data.messages).length > 0) {
              const formattedMessages = safeArray(data.messages).map((msg: any) => ({
                text: safeString(msg.content, JSON.stringify(msg)),
                isUser: false
              }));
              setMessages(formattedMessages);
            } else if (data.greeting || data.question) {
              const newMsgs: Message[] = [];
              if (data.greeting) newMsgs.push({ text: safeString(data.greeting), isUser: false });
              if (data.question) newMsgs.push({ text: safeString(data.question), isUser: false });
              setMessages(newMsgs);
            }
            if (data.isComplete && data.recommendation) {
              setIsChatComplete(true);
            }
            if (safeArray(data.formFields).length > 0) {
              setFormFields(safeArray(data.formFields));
              setIsChatComplete(true);
            }
          }
        } catch (error) {
          setMessages([{ text: 'Error starting chat. Please try again later.', isUser: false }]);
          setIsChatComplete(true);
        }
      }
    };
    if (!chatDisabled) startChat();
  }, [isOpen, chatDisabled]);

  const handleSendMessage = async () => {
    if (inputValue.trim() && !isChatComplete) {
      const answerValue = inputValue;
      const userMsg = { text: inputValue, isUser: true };
      setMessages(prev => [...prev, userMsg]);
      setIsLoading(true);
      try {
        setInputValue("");
        const response = await fetch(`${apiUrl}/chatbot/submit-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, answer: answerValue }),
        });
        let data: any = {};
        try {
          data = await response.json();
        } catch (e) {
          setMessages(prev => [...prev, { text: 'Sorry, failed to get a response. Please try again.', isUser: false }]);
          return;
        }
        if (!data || typeof data !== 'object') {
          setMessages(prev => [...prev, { text: 'Sorry, invalid response from server.', isUser: false }]);
          return;
        }
        if (safeArray(data.messages).length > 0) {
          const botMessages = safeArray(data.messages).map((msg: any) => ({
            text: safeString(msg.content, JSON.stringify(msg)),
            isUser: false
          }));
          setMessages(prev => [...prev, ...botMessages]);
        }
        if (safeArray(data.nextSteps).length > 0) {
          setAnyButtonClicked(false);
          setMessages(prev => [...prev, {
            text: 'Please select an option:',
            isUser: false,
            nextSteps: safeArray(data.nextSteps)
          }]);
        }
        if (data.isComplete && data.recommendation) {
          setIsChatComplete(true);
        }
        if (safeArray(data.formFields).length > 0) {
          setFormFields(safeArray(data.formFields));
          setIsChatComplete(true);
        }
      } catch (error) {
        setMessages(prev => [...prev, { text: 'Error sending message. Please try again.', isUser: false }]);
      } finally {
        setIsLoading(false);
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrl}/chatbot/store-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionId,
          formData: formData
        })
      });
      let data: any = {};
      try {
        data = await response.json();
      } catch (e) {
        setFormError('Sorry, failed to submit form. Please try again.');
        return;
      }
      if (data && data.status) {
        setMessages(prev => [...prev, {
          text: 'Thank you! A specialist will contact you shortly.',
          isUser: false
        }]);
        setFormFields([]);
        setFormData({});
        setFormError('');
      } else {
        setFormError(data && data.error && typeof data.error === 'string' && data.error.includes('Lead validation failed') ? 'Please fill valid details.' : 'Something went wrong.');
      }
    } catch (error) {
      setFormError('Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleReset = async () => {
    setMessages([]);
    setSessionId('');
    setIsChatComplete(false);
    setFormFields([]);
    setFormData({});
    setFormError('');
    setAnyButtonClicked(false);
    setInputValue('');
    try {
      const storedBusinessId = sessionStorage.getItem('wellnex_business_id');
      if (storedBusinessId) {
        await fetch(`${apiUrl}/chatbot/handle-consultation-choice`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId: sessionId,
            choice: 'decline'
          }),
        });
        const response = await fetch(`${apiUrl}/chatbot/start-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ businessId: storedBusinessId, isRestart: true }),
        });
        let data: any = {};
        try {
          data = await response.json();
        } catch (e) {
          setMessages([{ text: 'Sorry, failed to reset chat. Please try again.', isUser: false }]);
          setIsChatComplete(true);
          return;
        }
        if (!data || typeof data !== 'object') {
          setMessages([{ text: 'Sorry, invalid response from server.', isUser: false }]);
          setIsChatComplete(true);
          return;
        }
        if (data?.data?.isSetupIncomplete) {
          setMessages([{ text: `${safeString(data.messages)}\n\nContact: ${safeString(data.data.contactInfo)}`, isUser: false }]);
          setIsChatComplete(true);
          return;
        }
        setSessionId(safeString(data.sessionId));
        if (safeArray(data.messages).length > 0) {
          const formattedMessages = safeArray(data.messages).map((msg: any) => ({
            text: safeString(msg.content, JSON.stringify(msg)),
            isUser: false
          }));
          setMessages(formattedMessages);
        } else if (data.greeting || data.question) {
          const newMsgs: Message[] = [];
          if (data.greeting) newMsgs.push({ text: safeString(data.greeting), isUser: false });
          if (data.question) newMsgs.push({ text: safeString(data.question), isUser: false });
          setMessages(newMsgs);
        }
      }
    } catch (error) {
      setMessages([{ text: 'Sorry, there was an error resetting the chat. Please try again.', isUser: false }]);
    }
  };

  const handleNextStep = async (action: string) => {
    setAnyButtonClicked(true);
    const currentMessage = messages.find(msg => Array.isArray(msg.nextSteps));
    const stepObject = currentMessage && Array.isArray(currentMessage.nextSteps)
      ? currentMessage.nextSteps.find(step => step.action === action)
      : undefined;
    const stepLabel = stepObject && typeof stepObject.label === 'string' ? stepObject.label : action;
    setMessages(prev => [...prev, { text: stepLabel, isUser: true }]);
    setIsLoading(true);
    try {
      if (action === 'start_new') {
        await handleReset();
        return;
      }
      let endpoint = `${apiUrl}/chatbot/handle-consultation-choice`;
      let requestBody: any = {
        sessionId: sessionId,
        choice: action
      };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      let data: any = {};
      try {
        data = await response.json();
      } catch (e) {
        setMessages(prev => [...prev, { text: 'Sorry, failed to process your choice. Please try again.', isUser: false }]);
        return;
      }
      if (!data || typeof data !== 'object') {
        setMessages(prev => [...prev, { text: 'Sorry, invalid response from server.', isUser: false }]);
        return;
      }
      if (data.needsForm) {
        const messageText = safeString(data.message);
        if (messageText) {
          setMessages(prev => [...prev, { text: messageText, isUser: false }]);
        }
        if (safeArray(data.formFields).length > 0) {
          setFormFields(safeArray(data.formFields));
          setIsChatComplete(true);
        }
        return;
      }
      if (safeArray(data.messages).length > 0) {
        const botMessages = safeArray(data.messages).map((msg: any) => ({
          text: safeString(msg.content, JSON.stringify(msg)),
          isUser: false
        }));
        setMessages(prev => [...prev, ...botMessages]);
      }
      if (safeArray(data.nextSteps).length > 0) {
        setAnyButtonClicked(false);
        setMessages(prev => [...prev, {
          text: 'Please select an option:',
          isUser: false,
          nextSteps: safeArray(data.nextSteps)
        }]);
      }
      if (data.isComplete) {
        setIsChatComplete(true);
      }
      if (data.sessionEnded) {
        setMessages(prev => [...prev, {
          text: safeString(data.message, 'Thank you'),
          isUser: false
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { text: 'Sorry, there was an error processing your choice. Please try again.', isUser: false }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ChatbotErrorBoundary>
      {/* Welcome Popup */}
      {showWelcomePopup && !isOpen && (
        <div style={{
          position: 'fixed',
          bottom: 90,
          right: 60,
          background: '#DEE2E6',
          padding: '15px 20px',
          borderRadius: '10px 10px 0 10px',
          zIndex: 1002,
          maxWidth: '300px',
          animation: 'fadeInOut 5s ease-in-out',
          fontFamily: 'DM Sans',
          fontWeight: 600,
          fontSize: 20,
        }}>
          Welcome to our site, if you need help simply reply to this message, we are online and ready to help.
        </div >
      )}

      {/* Chat Icon Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setShowWelcomePopup(false);
        }}
        className="chat-icon-button"
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: themeColor,
          border: '1px solid #ccc',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          zIndex: 1000,
        }}
      >
        <img src={aiSparkChatbotAlt} alt="logo" width={30} />
      </button>

      {/* Chat Modal */}
      {
        isOpen && (
          <div style={{
            position: 'fixed',
            bottom: 90,
            right: 20,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '20px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.15)',
            width: '350px',
            height: '500px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1001
          }}>
            <div className="chat-container">
              {/* Header */}
              <header className="chat-header" style={{
                background: themeColor,
                borderBottom: '1px solid #ccc',
              }}>
                <div className="header-left">
                  <div className="logo">
                    <img src={logo ? `${uploadsUrl}/business-logos/${logo}` : '/chatbot.logo.png'} alt="logo" width={40} />
                  </div>
                  <div>
                    <h1 className="title" style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#fff',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      maxWidth: '200px'
                    }}>{name ? name : "Wellnex AI"}</h1>
                    <div className="status">
                      <div className="status-dot"></div>
                      <span className="status-text">Online</span>
                    </div>
                  </div>
                </div>
                <ResetButton onReset={handleReset} />
                <button
                  className="minimize-button"
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px'
                  }}
                >
                  <span className="minus-icon">
                    <img src={minusIcon} alt="logo" width={20} />
                  </span>
                </button>
              </header>

              {/* Chat Area or Form */}
              {isChatComplete && formFields.length > 0 ? (
                <div className="form-container" style={{ padding: '20px', overflowY: 'auto' }}>
                  <form onSubmit={handleFormSubmit}>
                    {formFields.map((field) => (
                      <div key={field.name} style={{ marginBottom: '15px' }}>
                        {field.type === 'checkbox' ? (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              name={field.name}
                              checked={formData[field.name] as boolean || false}
                              onChange={handleFormChange}
                              required={field.required}
                            />
                            <span>{field.label}</span>
                          </label>
                        ) : (
                          <>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                              {field.label}
                            </label>
                            <input
                              type={field.type}
                              name={field.name}
                              value={formData[field.name] as string || ''}
                              onChange={handleFormChange}
                              required={field.required}
                              placeholder={field.placeholder}
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc'
                              }}
                            />
                          </>
                        )}
                      </div>
                    ))}
                    <button
                      type="submit"
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: themeColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Submit
                    </button>
                  </form>
                  <div style={{ color: "red", textAlign: "center", paddingTop: "5px" }}>{formError}</div>
                </div>
              ) : (
                <div className="chat-messages" style={{ flex: 1, overflowY: 'auto' }}>
                  {messages.map((message, index) => (
                    <div key={index} className={`message-container ${message.isUser ? "user-message" : "bot-message"}`}>
                      <div className={`message-bubble ${message.isUser ? "user-bubble" : "bot-bubble"}`} style={{
                        background: !message.isUser ? themeColor : '#f0f0f0',
                        color: !message.isUser ? '#fff' : '#000',
                        position: 'relative',
                        [message.isUser ? 'right' : 'left']: '13px'
                      }}>
                        <p style={{ margin: 0 }}>
                          {typeof message.text === 'string' ? message.text : JSON.stringify(message.text)}
                        </p>
                        {message.nextSteps && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            marginTop: '12px'
                          }}>
                            {message.nextSteps.map((step, index) => (
                              <button
                                key={index}
                                onClick={() => handleNextStep(step.action)}
                                className="next-step-button"
                                disabled={anyButtonClicked}
                                style={{
                                  color: anyButtonClicked ? '#999' : themeColor,
                                  border: `1px solid ${anyButtonClicked ? '#999' : themeColor}`,
                                  cursor: anyButtonClicked ? 'not-allowed' : 'pointer',
                                  opacity: anyButtonClicked ? 0.7 : 1
                                }}
                              >
                                {step.label}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className={`message-triangle ${message.isUser ? "user-triangle" : "bot-triangle"}`}
                          style={{
                            position: 'absolute',
                            bottom: '-10px',
                            [message.isUser ? 'right' : 'left']: '0',
                            width: '0',
                            height: '0',
                            borderLeft: message.isUser ? '10px solid transparent' : '10px solid' + themeColor,
                            borderRight: message.isUser ? '10px solid #f0f0f0' : '10px solid ' + 'transparent',
                            borderTop: message.isUser ? '10px solid #f0f0f0' : '10px solid ' + themeColor,
                            borderBottom: '10px solid transparent',
                            zIndex: 1,
                          }}
                        />
                      </div>
                      {message.isUser ? (
                        <div className="user-avatar" style={{
                          background: themeColor,
                        }}>
                          <UserIcon color='white' />
                        </div>
                      ) : <div className="bot-avatar" style={{
                        background: themeColor,
                      }}>
                        <img src={logo ? `${uploadsUrl}/business-logos/${logo}` : '/chatbot.logo.png'} alt="bot-avatar" />
                      </div>}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="message-container bot-message">
                      <div className="message-bubble bot-bubble" style={{
                        background: themeColor,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        position: 'relative',
                        'left': '13px'
                      }}>
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>

                        <div className={`message-triangle bot-triangle`}
                          style={{
                            position: 'absolute',
                            bottom: '-10px',
                            'left': '0',
                            width: '0',
                            height: '0',
                            borderLeft: '10px solid' + themeColor,
                            borderRight: '10px solid ' + 'transparent',
                            borderTop: '10px solid ' + themeColor,
                            borderBottom: '10px solid transparent',
                            zIndex: 1,
                          }}
                        />
                      </div>
                      <div className="bot-avatar" style={{
                        background: themeColor,
                      }}>
                        <img src={logo ? "https://13.61.105.209/uploads/business-logos/" + logo : '/chatbot.logo.png'} alt="bot-avatar" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Input Area - Only show if chat is not complete */}
              {!isChatComplete && !chatDisabled && !messages.find(f => f.nextSteps) && (
                <div className="input-area">
                  <div className="input-container">
                    <input
                      type="text"
                      placeholder="Type your message here..."
                      className="message-input"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                    />
                    <button
                      onClick={handleSendMessage}
                      className="send-button"
                      disabled={isLoading}
                    >
                      <img src={sendIcon} alt="send" width={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(10px); }
            10% { opacity: 1; transform: translateY(0); }
            90% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }

          .typing-indicator {
            display: flex;
            gap: 4px;
          }

          .typing-indicator span {
            width: 8px;
            height: 8px;
            background: rgba(255, 255, 255, 0.7);
            border-radius: 50%;
            animation: bounce 1.4s infinite ease-in-out;
          }

          .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
          .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }

          .next-step-button {
            padding: 8px 16px;
            background: #fff;
            border-radius: 20px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
          }
        `}
      </style>
    </ChatbotErrorBoundary>
  );
};

export default Chatbot;

interface ResetButtonProps {
  onReset: () => void;
}

const ResetButton: React.FC<ResetButtonProps> = ({ onReset }) => {
  return (
    <button
      onClick={onReset}
      style={{
        padding: '8px 16px',
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        color: '#fff',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.2s ease',
        backdropFilter: 'blur(4px)',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
      Reset
    </button>
  );
};